<template>
  <div
    v-if="show"
    :class="['modal-overlay', { submitting: isSubmitting }]"
    @click="handleOverlayClick"
  >
    <div class="modal-content" @click.stop>
      <h2>
        {{
          formType === 'complaint'
            ? $t('communicationFormModal.complaintFormTitle')
            : $t('communicationFormModal.contactFormTitle')
        }}
      </h2>
      <form @submit.prevent="submit">
        <label for="name">{{ $t('communicationFormModal.nameLabel') }}</label>
        <input v-model="form.name" type="text" id="name" :disabled="isSubmitting" />
        <p v-if="errors.name" class="error">{{ errors.name }}</p>

        <label for="voucher">Voucher:</label>
        <input v-model="form.voucher" type="text" id="voucher" :disabled="isSubmitting" />
        <p v-if="errors.voucher" class="error">{{ errors.voucher }}</p>

        <label for="phone">{{ $t('communicationFormModal.phoneLabel') }}</label>
        <input v-model="form.phone" type="tel" id="phone" :disabled="isSubmitting" />
        <p v-if="errors.phone" class="error">{{ errors.phone }}</p>

        <label for="email">Email:</label>
        <input v-model="form.email" type="email" id="email" :disabled="isSubmitting" />
        <p v-if="errors.email" class="error">{{ errors.email }}</p>

        <label for="formMessage">{{
          formType === 'complaint'
            ? $t('communicationFormModal.complaintLabel')
            : $t('communicationFormModal.messageLabel')
        }}</label>
        <textarea
          v-model="form.formMessage"
          id="formMessage"
          rows="5"
          :disabled="isSubmitting"
        ></textarea>
        <p v-if="errors.formMessage" class="error">{{ errors.formMessage }}</p>

        <button type="submit" :disabled="isSubmitting">
          {{
            isSubmitting
              ? $t('communicationFormModal.submitting')
              : $t('communicationFormModal.submitButton')
          }}
        </button>
        <button type="button" @click="closeModal" class="close-btn" :disabled="isSubmitting">
          {{ $t('communicationFormModal.closeButton') }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import parsePhoneNumberFromString from 'libphonenumber-js';
import validator from 'validator';

export default {
  props: ['show', 'submitForm', 'formType'], // Added formType prop
  data() {
    return {
      form: {
        name: '',
        voucher: '',
        phone: '',
        email: '',
        formMessage: '',
      },
      errors: {},
      isSubmitting: false,
    };
  },
  watch: {
    formType(newType, oldType) {
      // Clear form and errors when switching between form types
      if (newType !== oldType) {
        this.clearForm();
        this.errors = {};
      }
    },
  },
  methods: {
    validateForm() {
      this.errors = {};

      if (validator.isEmpty(this.form.name.trim()) || !/[\w\u0370-\u03FF]+/.test(this.form.name)) {
        this.errors.name = this.$t('communicationFormModal.errorName');
      }

      // Validate voucher for both complaint and contact forms
      const slmTrackingRegex = /^[A-Za-z0-9]{13}$/; // 13 characters
      const cargusTrackingNumberRegex = /^\d{9}[-]\d{1,2}$/; // 9 digits - 1
      const cargusTrackingRegex = /^\d{9}$/; // 9 digits
      const cargusReturnTrackingRegex = /^(SR)\d{9,}([-]\d{1,2})?$/; // SR9+ digits
      const zaraTrackingRegex = /^\d{20}$/; // 20 digits
      const diakinisisTrackingRegex = /^[W]\d{7,9}[-]\d{1,2}$/; // W{soid}-1
      const dhlTrackingRegex = /^(?:00\d{18}|[1-4]?J[A-Z][A-Z0-9]{9,34}|[5-6]J[A-Z][A-Z0-9]{9,19})$/;
      const taxydemaTrackingRegex = /^\d{9,12}$/;

      const isValidVoucher =
        slmTrackingRegex.test(this.form.voucher) ||
        cargusTrackingNumberRegex.test(this.form.voucher) ||
        cargusTrackingRegex.test(this.form.voucher) ||
        cargusReturnTrackingRegex.test(this.form.voucher) ||
        zaraTrackingRegex.test(this.form.voucher) ||
        diakinisisTrackingRegex.test(this.form.voucher) ||
        dhlTrackingRegex.test(this.form.voucher) ||
        taxydemaTrackingRegex.test(this.form.voucher);

      if (!isValidVoucher) {
        this.errors.voucher = this.$t('communicationFormModal.errorVoucher');
      }

      const allowedCountries = [
        'GR',
        'CY',
        'AL',
        'BG',
        'GE',
        'IT',
        'RU',
        'TR',
        'GB',
        'DE',
        'FR',
        'ES',
        'US',
        'CA',
        'PT',
        'NL',
        'BE',
        'GI',
        'LU',
        'IE',
        'IS',
        'MT',
        'FI',
        'HU',
        'LT',
        'LV',
        'EE',
        'MD',
        'AM',
        'BY',
        'AD',
        'MC',
        'SM',
        'VA',
        'UA',
        'RS',
        'ME',
        'XK',
        'HR',
        'SI',
        'BA',
        'MK',
        'RO',
        'CH',
        'CZ',
        'SK',
        'LI',
        'AT',
        'DK',
        'SE',
        'NO',
        'PL',
        'AZ',
      ];
      const phoneNumber = parsePhoneNumberFromString(this.form.phone, {
        defaultCountry: 'GR',
      });

      if (!phoneNumber || !allowedCountries.includes(phoneNumber.country)) {
        this.errors.phone = this.$t('communicationFormModal.errorPhone');
      }

      if (!validator.isEmail(this.form.email)) {
        this.errors.email = this.$t('communicationFormModal.errorEmail');
      }

      if (
        validator.isEmpty(this.form.formMessage.trim()) ||
        !/[\w\u0370-\u03FF]+/.test(this.form.formMessage)
      ) {
        this.errors.formMessage =
          this.formType === 'complaint'
            ? this.$t('communicationFormModal.errorComplaint')
            : this.$t('communicationFormModal.errorMessage');
      }

      return Object.keys(this.errors).length === 0;
    },
    async submit() {
      if (!this.validateForm()) return;

      this.isSubmitting = true;

      try {
        const success = await this.submitForm(
          this.form.name,
          this.form.email,
          this.form.phone,
          this.form.voucher,
          this.form.formMessage,
          this.formType,
        );

        // Only clear form and close modal if submission was successful
        if (success) {
          this.clearForm();
        }
        this.closeModal();
      } catch (error) {
        console.error('Form submission failed:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    clearForm() {
      this.form.name = '';
      this.form.email = '';
      this.form.phone = '';
      this.form.voucher = '';
      this.form.formMessage = '';
    },
    handleOverlayClick() {
      if (!this.isSubmitting) {
        this.closeModal();
      }
    },
    closeModal() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Modal Styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.modal-overlay.submitting {
  cursor: not-allowed;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 10px;
  width: 400px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

label {
  display: block;
  margin-top: 10px;
}

input,
textarea {
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
}

input:disabled,
textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

button {
  margin-top: 15px;
  width: 100%;
  padding: 10px;
  background-color: #f58220;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  font-size: 16px;
  font-weight: normal;
  transition: opacity 0.2s;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.close-btn {
  background: gray;
  margin-top: 10px;
  font-size: 16px;
  font-weight: normal;
}

.error {
  color: red;
  font-size: 12px;
  margin-top: 2px;
}
</style>
